(function (root, factory) {
  var sharedProfile = root && root.NBBWatchProfileCore;
  if (typeof module === 'object' && module.exports) sharedProfile = require('../theme-shared/nbb-watch-profile-core.js');
  var api = factory(sharedProfile);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.NBBWelcomePopupCore = api;
})(typeof window !== 'undefined' ? window : null, function (sharedProfile) {
  'use strict';

  if (!sharedProfile) throw new Error('missing_watch_profile_core');
  var VARIANTS = ['premium', 'scratch'];
  var PROFILE_COMPLETIONS = ['started', 'partial', 'complete'];
  var ACTIONS = [
    'eligible',
    'teaser_viewed',
    'popup_opened',
    'step_viewed',
    'interaction_started',
    'scratch_started',
    'email_attempted',
    'signup_succeeded',
    'offer_revealed',
    'coupon_copied',
    'profile_started',
    'profile_back_clicked',
    'profile_step_saved',
    'profile_step_skipped',
    'profile_completed',
    'dismissed',
    'error'
  ];
  var ANALYTICS_KEYS = ['popupVersion', 'popupVariant', 'surface', 'pageType', 'locale', 'correlationId', 'errorCategory'];
  var SENSITIVE_KEY = /email|coupon|token|klaviyo|shopify|brand/i;
  var PROTECTED_ROUTE = /^\/(?:products|blogs|cart|checkout|account|challenge|password)(?:\/|$)/i;
  var LOCALE_PREFIX = /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i;

  function clean(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function normalizeEmail(value) {
    return clean(value).toLowerCase();
  }

  function isValidEmail(value) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalizeEmail(value));
  }

  function routeEligibility(pathname) {
    var route = clean(pathname) || '/';
    if (route.charAt(0) !== '/') route = '/' + route;
    route = route.replace(LOCALE_PREFIX, '') || '/';
    return PROTECTED_ROUTE.test(route)
      ? { eligible: false, reason: 'protected_route' }
      : { eligible: true, reason: 'eligible' };
  }

  function resolveVariant(input) {
    input = input || {};
    if (VARIANTS.indexOf(input.forcedVariant) > -1) return input.forcedVariant;
    if (VARIANTS.indexOf(input.storedVariant) > -1) return input.storedVariant;
    var rawAllocation = Number(input.allocation);
    var allocation = Number.isFinite(rawAllocation) ? Math.min(1, Math.max(0, rawAllocation)) : 0.5;
    var rawRandom = Number(input.randomValue);
    var randomValue = Number.isFinite(rawRandom) ? Math.min(1, Math.max(0, rawRandom)) : 0.5;
    return randomValue < allocation ? 'premium' : 'scratch';
  }

  function evaluateEligibility(input) {
    input = input || {};
    var route = routeEligibility(input.route);
    if (!route.eligible) return route;
    if (input.identified === true) return { eligible: false, reason: 'identified' };
    if (input.completed === true) return { eligible: false, reason: 'completed' };
    var now = Number(input.now);
    var dismissedAt = Number(input.dismissedAt);
    var cooldownMs = Math.max(0, Number(input.cooldownMs) || 0);
    if (Number.isFinite(now) && dismissedAt > 0 && now - dismissedAt < cooldownMs) {
      return { eligible: false, reason: 'cooldown' };
    }
    return { eligible: true, reason: 'eligible' };
  }

  function validSourceUrl(value) {
    var source = clean(value);
    if (!source || source.length > 2048) return false;
    try {
      var url = new URL(source);
      return url.protocol === 'https:' || (url.protocol === 'http:' && /^(?:localhost|127\.0\.0\.1)$/.test(url.hostname));
    } catch (_error) {
      return false;
    }
  }

  function buildSignupPayload(state, context) {
    state = state || {};
    context = context || {};
    var email = normalizeEmail(state.email);
    var variant = clean(state.variant);
    var locale = clean(context.locale);
    var sourceUrl = clean(context.sourceUrl);
    var experimentVersion = clean(context.experimentVersion);
    var turnstileToken = clean(context.turnstileToken);
    if (!isValidEmail(email)) throw new Error('invalid_email');
    if (context.consent !== true) throw new Error('consent_required');
    if (VARIANTS.indexOf(variant) === -1) throw new Error('invalid_variant');
    if (!/^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})?$/.test(locale)) throw new Error('invalid_locale');
    if (!validSourceUrl(sourceUrl)) throw new Error('invalid_source_url');
    if (experimentVersion !== 'commercial_popup_ab_v1') throw new Error('invalid_experiment');
    if (!turnstileToken || turnstileToken.length > 2048) throw new Error('invalid_turnstile_token');
    var payload = {
      email: email,
      consent: true,
      locale: locale,
      sourceUrl: sourceUrl,
      experimentVersion: experimentVersion,
      variant: variant,
      turnstileToken: turnstileToken
    };
    if (variant === 'scratch') {
      var rewardClaimToken = clean(context.rewardClaimToken);
      if (rewardClaimToken.length > 1024) throw new Error('invalid_reward_claim');
      payload.rewardClaimToken = rewardClaimToken;
    }
    return payload;
  }

  function buildProfilePayload(profileClaimToken, answers, completion) {
    var claim = clean(profileClaimToken);
    if (!claim || claim.length > 512) throw new Error('invalid_profile_claim');
    if (PROFILE_COMPLETIONS.indexOf(completion) === -1) throw new Error('invalid_profile_completion');
    return {
      profileClaimToken: claim,
      answers: sharedProfile.normalizeAnswers(answers),
      completion: completion
    };
  }

  function analyticsDetail(action, input) {
    input = input || {};
    if (ACTIONS.indexOf(action) === -1) throw new Error('invalid_analytics_action');
    Object.keys(input).forEach(function (key) {
      if (ANALYTICS_KEYS.indexOf(key) === -1 || SENSITIVE_KEY.test(key)) throw new Error('unsafe_analytics');
    });
    var variant = clean(input.popupVariant);
    var surface = clean(input.surface);
    if (variant && VARIANTS.indexOf(variant) === -1) throw new Error('unsafe_analytics');
    if (surface && ['modal', 'teaser'].indexOf(surface) === -1) throw new Error('unsafe_analytics');
    var detail = {
      event: 'nbb_welcome_popup',
      popup_action: action,
      popup_version: clean(input.popupVersion),
      popup_variant: variant,
      surface: surface,
      page_type: clean(input.pageType),
      locale: clean(input.locale)
    };
    if (clean(input.correlationId)) detail.correlation_id = clean(input.correlationId);
    if (clean(input.errorCategory)) detail.error_category = clean(input.errorCategory);
    return detail;
  }

  function scratchedRatio(imageData, sampleStep) {
    if (!imageData || !imageData.width || !imageData.height || !imageData.data) return 0;
    var stride = Math.max(1, Math.floor(Number(sampleStep) || 1));
    var erased = 0;
    var sampled = 0;
    for (var y = 0; y < imageData.height; y += stride) {
      for (var x = 0; x < imageData.width; x += stride) {
        sampled += 1;
        if (imageData.data[(y * imageData.width + x) * 4 + 3] < 128) erased += 1;
      }
    }
    return sampled ? erased / sampled : 0;
  }

  return {
    VARIANTS: VARIANTS.slice(),
    PROFILE_COMPLETIONS: PROFILE_COMPLETIONS.slice(),
    ACTIONS: ACTIONS.slice(),
    normalizeEmail: normalizeEmail,
    isValidEmail: isValidEmail,
    routeEligibility: routeEligibility,
    resolveVariant: resolveVariant,
    evaluateEligibility: evaluateEligibility,
    buildSignupPayload: buildSignupPayload,
    buildProfilePayload: buildProfilePayload,
    analyticsDetail: analyticsDetail,
    scratchedRatio: scratchedRatio
  };
});
